import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { cfRegionLinks } from '../core/helper/cfRegionLinks';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { CustomerdatabaseService } from './customerdatabase.service';
import { ApplicantService } from './applicant-service.service';
import { JobOfferService } from './jobOfferService.service';
import { FirebaseService } from './firebase.service';
import { MenuService } from './menu-service';
import { ProfileService } from './profile.service';
import { MenuController, ToastController } from '@ionic/angular';
import * as toolInfos from '../mona-agent-builder/tool_infos_mapping.json';
import { resolvePlatformLanguage } from '../core/helper/platform-language.helper';
import { isHybridPlan, resolveMenuRoleForPlan } from '../core/helper/plan-routing.helper';
import { isPartnerLaunch, partnerLaunchEnv } from '../ars-embed/partner-launch-sandbox';
import { partnerApiBase } from '../ars-embed/partner-launch';

@Injectable({
  providedIn: 'root'
})
export class SessionInitService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private customerDB: CustomerdatabaseService,
    private applicantService: ApplicantService,
    private jobOfferService: JobOfferService,
    private firebaseService: FirebaseService,
    private menuService: MenuService,
    private profileService: ProfileService,
    private menuCtrl: MenuController,
    private router: Router,
    private toastController: ToastController
  ) { }

  private autoLoginToast: any = null;
  private cachedLoginResponse: any = null;
  private cacheExpiry: number = 0;

  // Gate for automatic (cookie/token) login. Resets on every full page load
  // because this service is re-created on reload. Only set to true when the
  // user explicitly triggers a login (e.g. clicks "Anmelden" on the login page).
  private userInitiatedLogin = false;

  markUserInitiatedLogin() {
    this.userInitiatedLogin = true;
  }

  hasUserInitiatedLogin(): boolean {
    return this.userInitiatedLogin;
  }

  clearLoginCache() {
    this.cachedLoginResponse = null;
    this.cacheExpiry = 0;
  }

  private async showAutoLoginToast(message: string = 'Logging you in...') {
    if (this.autoLoginToast) return;
    this.autoLoginToast = await this.toastController.create({
      message,
      position: 'top',
      color: 'primary',
    });
    await this.autoLoginToast.present();
  }

  private async dismissAutoLoginToast() {
    try {
      if (this.autoLoginToast) {
        await this.autoLoginToast.dismiss();
      }
    } catch { }
    this.autoLoginToast = null;
  }

  async autoLoginFromCookieAndInitialize(): Promise<boolean> {
    console.log('[Auto-login] Starting auto-login process');
    const currentUser = await this.authService.waitForAuthState();
    if (!currentUser) {
      console.log('[Auto-login] No active Firebase session found');
      return false;
    }
    const res = await this.autoLoginWithFirebaseSession(currentUser);
    return res;
  }

  async minimalAgentsLogin(currentUser: any, partnerPlan?: string): Promise<boolean> {
    try {
      console.log('[MinimalAgentsLogin] Ultra-fast login for agents.monaai.de');

      // 1. Save basic user info
      const uid = currentUser.uid;
      const email = currentUser.email;
      this.authService.saveUserId(uid);
      this.authService.saveEmail(email);

      // 2. Get ID token
      const idToken = await this.authService.getIdToken();
      if (!idToken) {
        console.error('[MinimalAgentsLogin] No ID token');
        return false;
      }

      // 3. Check cache first, then call login platform if needed
      let loginResp: any;
      const now = Date.now();

      if (this.cachedLoginResponse && this.cacheExpiry > now && this.cachedLoginResponse.uid === uid) {
        console.log('[MinimalAgentsLogin] Using cached login response');
        loginResp = this.cachedLoginResponse;
      } else {
        console.log('[MinimalAgentsLogin] Calling login platform');
        const loginUrl = isPartnerLaunch() ? `${partnerApiBase(partnerLaunchEnv() || 'test')}/api/loginPlatform` : environment.loginPlatform;
        loginResp = await this.http.post(loginUrl, {
          uid: uid,
          email: email,
          idToken: idToken
        }).toPromise();

        if (loginResp && loginResp.database) {
          // Cache for 5 minutes
          this.cachedLoginResponse = { ...loginResp, uid: uid };
          this.cacheExpiry = now + (7 * 24 * 60 * 60 * 1000); // saved for 5 days
        }
      }

      if (!loginResp || !loginResp.database) {
        console.error('[MinimalAgentsLogin] Invalid login response');
        return false;
      }

      // 4. Handle custom token if present (but don't sign in again - already authenticated)
      if (loginResp.customToken) {
        this.authService.setCustomTokenObj(loginResp.customToken);
        console.log('[MinimalAgentsLogin] Saved custom token for future use');
      }

      // 5. Connect to database (skip profile loading for minimal setup)
      const dbConnected = await this.customerDB.connectDB(loginResp, true);
      //connect the storage.
      await this.customerDB.setUserStorage(uid);
      if (!dbConnected) {
        console.error('[MinimalAgentsLogin] Database connection failed');
        return false;
      }

      // 6. Set basic customer data without loading storage
      if (loginResp.kundennummer) {
        this.authService.saveKundennummer(loginResp.kundennummer);
      }
      if (loginResp.account_id) {
        this.authService.saveAccountId(loginResp.account_id);
      }

      // 7. Save user ID directly (skip full user storage setup)
      this.customerDB.userId = uid;

      const isSelfServiceCustomer = loginResp.isSelfServiceCustomer || false;
      const kundennummer = loginResp.kundennummer;
      let currentPlan = '';

      const isEnterpriseCustomer = kundennummer && kundennummer !== 100001 && kundennummer !== 100002 && kundennummer !== 0;

      if (isPartnerLaunch()) {
        currentPlan = partnerPlan || '';
      } else if (isEnterpriseCustomer) {
        currentPlan = 'enterprise';
        console.log('[MinimalAgentsLogin] Enterprise customer detected (kundennummer:', kundennummer, '), setting plan to enterprise');
      } else {
        try {
          currentPlan = await this.firebaseService.retreiveUserPlan(email) || '';
          console.log('[MinimalAgentsLogin] Retrieved plan from Firebase:', currentPlan);
        } catch (e) {
          console.error('[MinimalAgentsLogin] Failed to retrieve plan from Firebase:', e);
        }

        if (!currentPlan) {
          console.log('[MinimalAgentsLogin] No plan found in Firebase, will route to pricing');
        }
      }

      this.customerDB.setcurrentPlan(currentPlan);
      this.authService.saveUserPlan(currentPlan);
      console.log('[MinimalAgentsLogin] Set current plan:', currentPlan);

      // 9. Generate API key (but don't block on it)
      this.customerDB.generateFreshApiKey()
        .then(apiKey => {
          this.authService.saveApiKey(apiKey);
          console.log('[MinimalAgentsLogin] API key generated');
        })
        .catch(e => {
          console.error('[MinimalAgentsLogin] API key generation failed', e);
        });

      // 10. Set minimal required properties
      this.authService.verifySessionCookie(true);

      // 11. Mark as authenticated immediately
      this.authService.setAuthenticated(true);

      console.log('[MinimalAgentsLogin] Login complete');
      return true;
    } catch (error) {
      console.error('[MinimalAgentsLogin] Error:', error);
      return false;
    }
  }

  async autoLoginWithFirebaseSession(currentUser: any): Promise<boolean> {
    try {
      console.log('[Auto-login] Using active Firebase session for user:', currentUser.uid);

      // Save user info exactly like login does
      const uid = currentUser.uid;
      const email = currentUser.email;
      this.customerDB.setUserEmail(email);

      if (email.includes('@mona-ai.de')) {
        this.authService.saveUserRole('admin');
      } else {
        this.authService.saveUserRole('user');
      }

      this.authService.saveUserId(uid);
      this.authService.saveEmail(email);

      // Run ID token retrieval and login platform call in parallel
      const [idToken, _] = await Promise.all([
        this.authService.getIdToken(),
        this.menuCtrl.enable(true)
      ]);

      if (!idToken) {
        console.error('[Auto-login] Failed to get ID token from Firebase session');
        return false;
      }

      console.log('[Auto-login] Got ID token, calling login platform...');

      // Call login platform exactly like the login flow does
      const sendObject = {
        uid: uid,
        email: email,
        ip: '',
        userAgent: '',
        language: '',
        idToken: idToken
      };

      const loginResp: any = await this.http.post(environment.loginPlatform, sendObject, {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*'
        }
      }).toPromise();

      console.log('[Auto-login] Login platform response received');

      // Handle custom token metadata, but do not re-authenticate if already signed in
      if (loginResp && loginResp.customToken) {
        this.authService.setCustomTokenObj(loginResp.customToken);
        // Ensure firebaseAuthToken cookie is set for backend that relies on it
        try { await this.authService.getLocalToken(); } catch { }
        // Proceed to generate API key and continue initialization
        try {
          const apiKey = await this.customerDB.generateFreshApiKey();
          this.authService.saveApiKey(apiKey);
          console.log('[Auto-login] Generated and saved API key');
        } catch (e) {
          console.error('[Auto-login] API key generation failed', e);
        }
      }

      // Connect to database (exactly like login does)
      if (loginResp && loginResp.database !== undefined && loginResp.kundennummer !== 0) {
        const snap = await this.customerDB.connectDB(loginResp);
        console.log('[Auto-login] connectDB result:', snap);

        if (snap === true) {
          // Initialize storage
          await this.customerDB.setUserStorage(uid);

          // Check/create user
          const userExists = await this.customerDB.checkIfUsersExists(uid);
          if (userExists === false) {
            this.customerDB.createuserInsideUsers(uid);
          }

          // Resolve the teammember doc once up-front and persist both locations and status
          // to the profile service BEFORE any downstream code (applicants, job offers, menu)
          // reads them. Delegates to ProfileService which does case-insensitive email lookup
          // and unconditional save so mandants are populated even when status is missing.
          let teamMemberLocations: string[] | null = null;

          try {
            const { locations, status } = await this.profileService.loadTeamMemberDataForEmail(this.customerDB, email);

            if (status != null && status !== undefined) {
              this.menuService.setUserRoleByRole(status);
              console.log('[Auto-login] Set team member role:', status, 'locations:', locations);
            }

            if (locations.length > 0) {
              const isAdminEmail = email.includes('@mona-ai.de') || email.includes('@mona-ai.services');
              const isAdminRole = status == 5 || status === 'SuperAdmin' || status === 'admin';
              if (!isAdminEmail && !isAdminRole) {
                teamMemberLocations = locations;
              }
            }
          } catch (err) {
            console.error('[Auto-login] Error checking team member status (pre-fetch):', err);
          }

          // Pass the array of allowed locations to the applicants API so filtering happens server-side.
          const applicantsLocationArg: string[] | null = teamMemberLocations && teamMemberLocations.length > 0 ? teamMemberLocations : null;

          // Load applicants and job offers in parallel
          const [applicantsResult, jobOffersProcessed] = await Promise.all([
            this.customerDB.getApplicantsWithCreatedFromAPI(null, null, applicantsLocationArg).catch(() => ({ data: [] })),
            this.customerDB.getJobOffersFromAPI().catch(() => ({ data: [] }))
          ]);

          // Process applicants
          const allApplicants = applicantsResult?.data || [];
          this.applicantService.getAllApplicants(allApplicants);

          // Process job offers
          this.jobOfferService.allJobOffers = jobOffersProcessed?.data || [];
          this.jobOfferService.jobOffers = jobOffersProcessed?.data || [];

          // Save customer info
          const customerNumber = this.customerDB.getCustomerNumber();
          this.authService.saveKundennummer(customerNumber);
          this.authService.saveAccountId(loginResp.account_id);
          this.authService.saveLicenses(loginResp.licenses);

          let currentPlan = '';
          const isSelfServiceCustomer = loginResp.isSelfServiceCustomer || false;
          const kundennummer = loginResp.kundennummer;

          const isEnterpriseCustomer = kundennummer && kundennummer !== 100001 && kundennummer !== 100002 && kundennummer !== 0;

          if (isEnterpriseCustomer) {
            currentPlan = 'enterprise';
            console.log('[Auto-login] Enterprise customer detected (kundennummer:', kundennummer, '), setting plan to enterprise');
          } else {
            try {
              currentPlan = await this.firebaseService.retreiveUserPlan(email) || '';
              console.log('[Auto-login] Retrieved plan from Firebase:', currentPlan);
            } catch (e) {
              console.error('[Auto-login] Failed to retrieve plan from Firebase:', e);
            }

            if (!currentPlan) {
              console.log('[Auto-login] No plan found in Firebase, will route to pricing');
            }
          }

          this.customerDB.setcurrentPlan(currentPlan);
          this.authService.saveUserPlan(currentPlan);
          console.log('[Auto-login] Set current plan:', currentPlan);


          // Initialize menu based on plan or customer type
          console.log('[Auto-login] Setting menu for plan:', currentPlan);
          if (currentPlan) {
            const role = resolveMenuRoleForPlan(currentPlan, {
              kundennummer: this.customerDB.getCustomerNumber(),
              canAccessHybrid: this.customerDB.canAccessHybridFeatures()
            });
            console.log('[Auto-login] Menu role:', role, 'hasHybridAddon:', this.customerDB.hasHybridAddon(), 'userHybridAccess:', this.customerDB.hasHybridAccess());
            this.menuService.setMenuForRole(role);
            if (isHybridPlan(currentPlan)) {
              this.loadMonaAppsToMenu();
            }
          }

          // Menu already enabled in parallel earlier

          // Ensure branding exists for this account
          try {
            const exists = await this.checkBrandingInfoExists();
            if (!exists) {
              const allowedTools = Object.values((toolInfos as any) || {}).reduce((acc: any, t: any) => {
                if (t && t.name) acc[t.name] = true;
                return acc;
              }, {});
              await this.customerDB.createProfile({
                firmenname: '',
                webseite: '',
                companyLogo: '',
                colorCode: '#fe413b',
                email: email,
                password: '',
                // Seed the branding language from the language chosen during
                // signup/pricing (mirrored into platformLanguage) instead of leaving it unset.
                platformLanguage: resolvePlatformLanguage(localStorage.getItem('platformLanguage')),
                allowed_tools: allowedTools,
                fallbackInterviewId: '',
                interviewFinishedURL: '',
                euMatching: false,
                minimumMatchingResult: 0,
                sendMatches: false,
                chosenCriteria: [],
                mustHaves: [],
                personalizedMessage: '',
                dataProtection: '',
                standorte: []
              });
            }
            // Clean up any duplicate branding documents from prior race conditions
            await this.customerDB.cleanupDuplicateBranding();
            // Check SMTP presence and create if missing
            const profileCollection = this.customerDB.getProfile();
            if (profileCollection) {
              const brandingSnap = await profileCollection.get();
              if (!brandingSnap.empty) {
                const brandingDoc = brandingSnap.docs[0];
                const brandingData = brandingDoc.data();
                if (!brandingData.smtpEmail) {
                  try {
                    const nicknameBase = (email.split('@')[0] || 'user').toLowerCase();
                    const nickname = nicknameBase.replace(/[^a-z0-9._-]/g, '.');
                    const industry = '';
                    const res = await (this.customerDB as any).sendRequestToCreateEmail(nickname, industry);
                    if (res && res.email && res.password) {
                      await (profileCollection as any).doc(brandingDoc.id).set({
                        smtpEmail: res.email,
                        smtpHost: 'mail.mona-ai.online',
                        smtpPort: 465,
                        smtpPassword: res.password
                      }, { merge: true });
                    }
                  } catch { }
                }
              }
            }
          } catch { }

          // No navigation redirect; leave redirect to LoginPage.checkAutoLogin()

          // Force authentication state update
          console.log('[Auto-login] Updating auth state');
          this.authService.verifySessionCookie(true);

          console.log('[Auto-login] Successfully completed auto-login with menu initialization');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('[Auto-login] Failed to auto-login with Firebase session:', error);
      return false;
    }
  }

  private async ensureSmtpExists(email: string): Promise<void> {
    try {
      const profileCollection = this.customerDB.getProfile();
      if (!profileCollection) return;
      const brandingSnap = await profileCollection.get();
      if (brandingSnap.empty) return;
      const brandingDoc = brandingSnap.docs[0];
      const brandingData = brandingDoc.data();
      if (brandingData.smtpEmail) return;
      const nicknameBase = (email.split('@')[0] || 'user').toLowerCase();
      const nickname = nicknameBase.replace(/[^a-z0-9._-]/g, '.');
      const res = await (this.customerDB as any).sendRequestToCreateEmail(nickname, '');
      if (res && res.email && res.password) {
        await (profileCollection as any).doc(brandingDoc.id).set({
          smtpEmail: res.email,
          smtpHost: 'mail.mona-ai.online',
          smtpPort: 465,
          smtpPassword: res.password
        }, { merge: true });
      }
    } catch { }
  }

  private async checkBrandingInfoExists(): Promise<boolean> {
    try {
      const profileCollection = this.customerDB.getProfile();
      if (!profileCollection) {
        return false;
      }
      const profileSnapshot = await profileCollection.get();
      if (!profileSnapshot.empty) {
        return true;
      }
      let docsLength = profileSnapshot.docs.length;
      if (docsLength > 0) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async loadMonaAppsToMenu() {
    try {
      const apiData: any = await this.customerDB.getAPIData();
      if (apiData && apiData.monaApps && Array.isArray(apiData.monaApps)) {
        const savedApps: any[] = apiData.monaApps;
        savedApps.forEach(saved => {
          this.menuService.addModuleMenuItem({
            title: saved.title,
            url: saved.url,
            icon: saved.icon
          });
        });
      }
    } catch (err) {
      console.error('[Session-init] Error loading MONA apps to menu:', err);
    }
  }

}



